import * as RDialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { useRef, useState, type ReactNode } from 'react';
import { Botao, BotaoIcone } from './Botao';

export interface DialogoProps {
  aberto: boolean;
  aoFechar: () => void;
  titulo: string;
  descricao?: string;
  largura?: 'p' | 'm' | 'g';
  rodape?: ReactNode;
  children?: ReactNode;
  /** Elemento que recebe o foco ao abrir (padrão: primeiro focável). */
  focoInicial?: React.RefObject<HTMLElement | null>;
  className?: string;
}

/** role="dialog", aria-modal, foco preso e devolvido à origem ao fechar (07 §7) — pelo Radix. */
export function Dialogo({ aberto, aoFechar, titulo, descricao, largura = 'm', rodape, children, focoInicial, className }: DialogoProps) {
  return (
    <RDialog.Root open={aberto} onOpenChange={(a) => !a && aoFechar()}>
      <RDialog.Portal>
        <RDialog.Overlay className="sobreposicao" />
        <RDialog.Content
          className={`dialogo folha ${className ?? ''}`}
          data-largura={largura}
          aria-describedby={descricao ? undefined : undefined}
          onOpenAutoFocus={(e) => {
            if (focoInicial?.current) {
              e.preventDefault();
              focoInicial.current.focus();
            }
          }}
        >
          <div className="dialogo-cabecalho">
            <RDialog.Title className="tp-secao" style={{ margin: 0 }}>{titulo}</RDialog.Title>
            <RDialog.Close asChild>
              <BotaoIcone rotulo="Fechar (Esc)" icone={<X size={18} strokeWidth={1.75} />} />
            </RDialog.Close>
          </div>
          {descricao ? <RDialog.Description className="dialogo-corpo" style={{ margin: 0 }}>{descricao}</RDialog.Description> : <RDialog.Description className="sr-only">{titulo}</RDialog.Description>}
          {children && <div className="dialogo-corpo">{children}</div>}
          {rodape && <div className="dialogo-rodape">{rodape}</div>}
        </RDialog.Content>
      </RDialog.Portal>
    </RDialog.Root>
  );
}

export interface DialogoConfirmarProps {
  aberto: boolean;
  titulo: string;
  texto: string;
  rotuloConfirmar: string;
  rotuloVoltar?: string;
  perigo?: boolean;
  /** Exige digitar este texto para liberar a confirmação (operação irreversível). */
  digitarParaConfirmar?: string;
  aoConfirmar: () => void | Promise<void>;
  aoFechar: () => void;
}

/** Voltar é o padrão e recebe o foco (07 §2.7). */
export function DialogoConfirmar({ aberto, titulo, texto, rotuloConfirmar, rotuloVoltar = 'Voltar', perigo, digitarParaConfirmar, aoConfirmar, aoFechar }: DialogoConfirmarProps) {
  const voltar = useRef<HTMLButtonElement>(null);
  const [digitado, setDigitado] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const liberado = !digitarParaConfirmar || digitado === digitarParaConfirmar;
  return (
    <Dialogo
      aberto={aberto}
      aoFechar={aoFechar}
      titulo={titulo}
      descricao={texto}
      largura="p"
      focoInicial={voltar}
      rodape={
        <>
          <Botao ref={voltar} onClick={aoFechar}>{rotuloVoltar}</Botao>
          <Botao
            variante={perigo ? 'perigo' : 'primario'}
            disabled={!liberado}
            carregando={ocupado}
            onClick={async () => {
              setOcupado(true);
              try {
                await aoConfirmar();
              } finally {
                setOcupado(false);
              }
            }}
          >
            {rotuloConfirmar}
          </Botao>
        </>
      }
    >
      {digitarParaConfirmar && (
        <label className="campo" style={{ marginTop: 8 }}>
          <span className="campo-rotulo">Digite <strong className="mono">{digitarParaConfirmar}</strong> para confirmar</span>
          <span className="campo-controle"><input value={digitado} onChange={(e) => setDigitado(e.target.value)} /></span>
        </label>
      )}
    </Dialogo>
  );
}

export interface DialogoLivreProps {
  aberto: boolean;
  aoFechar: () => void;
  className?: string;
  focoInicial?: React.RefObject<HTMLElement | null>;
  children: ReactNode;
}

/**
 * Diálogo sem cabeçalho fixo, para telas com composição própria (o "O que mudou"). Mesmo comportamento do <c>Dialogo</c>: foco preso e
 * devolvido, Esc fecha. O conteúdo usa <c>DialogoLivreTitulo</c>, <c>DialogoLivreDescricao</c> e <c>DialogoLivreFechar</c>.
 */
export function DialogoLivre({ aberto, aoFechar, className, focoInicial, children }: DialogoLivreProps) {
  return (
    <RDialog.Root open={aberto} onOpenChange={(a) => !a && aoFechar()}>
      <RDialog.Portal>
        <RDialog.Overlay className="sobreposicao" />
        <RDialog.Content
          className={`dialogo folha ${className ?? ''}`}
          onOpenAutoFocus={(e) => {
            if (focoInicial?.current) {
              e.preventDefault();
              focoInicial.current.focus();
            }
          }}
        >
          {children}
        </RDialog.Content>
      </RDialog.Portal>
    </RDialog.Root>
  );
}

export const DialogoLivreTitulo = RDialog.Title;
export const DialogoLivreDescricao = RDialog.Description;
export const DialogoLivreFechar = RDialog.Close;
